import { useEffect } from "react";

const STORAGE_PREFIX = "listFilterState:";

// Reads whatever filter state was last persisted for this page, or null if
// there's nothing stored yet (or it fails to parse — an older/incompatible
// shape from a previous app version, say).
export function readPersistedFilterState(pageKey) {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${pageKey}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Keeps the given filter-related state snapshot mirrored into sessionStorage
// under this page's key, so navigating away to edit a record and back
// (browser back, or the app's own navigate(-1) "back" buttons) restores the
// same applied filter — whether it came from the ad-hoc FilterBox or from
// SavedFiltersPanel, both just call setFilterOptions/setSearchText the same
// way, so persisting this state covers both uniformly. sessionStorage
// (not the URL or localStorage) is deliberate: this is meant to survive only
// the current tab's session, not persist indefinitely or become a
// shareable/bookmarkable link.
// `deps` is the effect's own dependency array (typically the same values
// referenced inside `state`, e.g. [filterOptions, searchText, filterPills,
// currentPage]) — passed explicitly rather than derived from `state` itself
// so this doesn't depend on Object.values' key-order behavior.
export function usePersistFilterState(pageKey, state, deps) {
  useEffect(() => {
    try {
      sessionStorage.setItem(`${STORAGE_PREFIX}${pageKey}`, JSON.stringify(state));
    } catch {
      // sessionStorage can throw (private browsing, storage full) — losing
      // the "remember filters for this visit" convenience is harmless, so
      // just no-op rather than surface an error to the admin.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
